import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useUser } from '../context/UserContext';
import bookingAPI from '../services/bookingApi';

const HomePage = () => {
  const navigate = useNavigate();
  const { selectHall } = useBooking();
  const { user, logout } = useUser();
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { loadHalls(); }, []);

  const loadHalls = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getHalls();
      if (response.success) setHalls(response.data);
      else alert('Failed to load halls');
    } catch {
      alert('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleHallSelect = (hall) => {
    if (!hall.isAvailable) {
      alert(`${hall.name} is currently unavailable. Please select another hall or contact admin.`);
      return;
    }
    selectHall(hall);
    navigate('/time-slot');
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/', { replace: true });
    }
  };

  return (
    <div style={s.page}>
      {/* Navbar */}
      <header style={s.navbar}>
        <div style={s.navInner}>
          <div style={s.navBrand}>
            <div style={s.navLogoBox}>
              <img src="/logo.png" alt="Logo" style={s.navLogo}
                onError={e => { e.target.parentElement.style.display = 'none'; }} />
            </div>
          </div>
          <div style={s.navActions}>
            <button style={s.navBtnOutline} onClick={() => navigate('/my-bookings')}>
              My Bookings
            </button>
            <button style={s.navBtnGhost} onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Page header */}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderInner}>
          <h1 style={s.pageTitle}>Seminar Hall Booking</h1>
          <p style={s.pageSubtitle}>
            Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Select a hall to begin your booking.
          </p>
        </div>
      </div>

      {/* Divider */}
      <div style={s.divider} />

      {/* Content */}
      <main style={s.main}>
        {loading ? (
          <div style={s.loadingWrap}>
            <div style={s.spinner} />
            <p style={s.loadingText}>Loading halls...</p>
          </div>
        ) : halls.length === 0 ? (
          <div style={s.emptyWrap}>
            <p style={s.emptyText}>No halls available at the moment.</p>
          </div>
        ) : (
          <div style={{
            ...s.grid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))'
          }}>
            {halls.map(hall => (
              <div key={hall.id} style={s.card}>
                {/* Colored top accent bar */}
                <div style={{ ...s.cardAccent, background: hall.isAvailable ? '#4f46e5' : '#d1d5db' }} />

                <div style={s.cardBody}>
                  {/* Status badge */}
                  <span style={hall.isAvailable ? s.badgeAvail : s.badgeUnavail}>
                    {hall.isAvailable ? 'Available' : 'Unavailable'}
                  </span>

                  {/* Hall name */}
                  <h2 style={s.hallName}>{hall.name}</h2>

                  {/* Description */}
                  {hall.description && (
                    <p style={s.description}>{hall.description}</p>
                  )}

                  {/* Tags */}
                  <div style={s.tagsRow}>
                    <div style={s.tag}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      {hall.location}
                    </div>
                    <div style={s.tag}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
                      {hall.capacity} seats
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={s.cardFooter}>
                  <button
                    style={hall.isAvailable ? s.bookBtn : s.bookBtnDisabled}
                    onClick={() => handleHallSelect(hall)}
                    disabled={!hall.isAvailable}
                  >
                    {hall.isAvailable ? 'Book Now →' : 'Currently Unavailable'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        button:hover:not(:disabled) { opacity: 0.88; }
      `}</style>
    </div>
  );
};

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    color: '#111827',
  },

  /* Navbar */
  navbar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 20px',
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  navLogoBox: {
    flexShrink: 0,
  },
  navLogo: {
    height: 44,
    width: 'auto',
    display: 'block',
  },
  navActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  navBtnOutline: {
    padding: '7px 16px',
    border: '1.5px solid #4f46e5',
    borderRadius: 8,
    backgroundColor: 'transparent',
    color: '#4f46e5',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  navBtnGhost: {
    padding: '7px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 8,
    backgroundColor: 'transparent',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },

  /* Page header */
  pageHeader: {
    backgroundColor: '#ffffff',
    padding: '28px 20px 24px',
  },
  pageHeaderInner: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 6px',
  },
  pageSubtitle: {
    fontSize: 15,
    color: '#6b7280',
    margin: 0,
  },

  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
  },

  /* Main content */
  main: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '32px 20px 60px',
  },

  /* Loading */
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px 0',
    gap: 14,
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #4f46e5',
    borderRadius: '50%',
    animation: 'spin 0.9s linear infinite',
  },
  loadingText: {
    fontSize: 15,
    color: '#6b7280',
    margin: 0,
  },

  /* Empty */
  emptyWrap: {
    textAlign: 'center',
    padding: '100px 0',
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
  },

  /* Grid */
  grid: {
    display: 'grid',
    gap: 20,
  },

  /* Card */
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardAccent: {
    height: 5,
    width: '100%',
  },
  cardBody: {
    padding: '18px 20px 14px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  hallName: {
    fontSize: 17,
    fontWeight: 700,
    color: '#111827',
    margin: '8px 0 8px',
  },
  badgeAvail: {
    alignSelf: 'flex-start',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: '#ecfdf5',
    color: '#059669',
    border: '1px solid #a7f3d0',
  },
  badgeUnavail: {
    alignSelf: 'flex-start',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
  },
  description: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.6,
    margin: '0 0 14px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 'auto',
  },
  tag: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    backgroundColor: '#eff0fe',
    border: '1px solid #c7d2fe',
    borderRadius: 20,
    fontSize: 12,
    color: '#4338ca',
    fontWeight: 500,
  },
  cardFooter: {
    padding: '12px 20px 16px',
    borderTop: '1px solid #f3f4f6',
  },
  bookBtn: {
    width: '100%',
    padding: '10px 0',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  bookBtnDisabled: {
    width: '100%',
    padding: '10px 0',
    backgroundColor: '#f3f4f6',
    color: '#9ca3af',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'not-allowed',
    fontFamily: 'inherit',
  },
};

export default HomePage;
